/**
 * Toast - Notification toast component for displaying alerts
 *
 * Features:
 * - Auto-dismiss with configurable duration
 * - Multiple toast types (success, error, info, warning)
 * - Animated entry/exit
 * - Click to dismiss
 * - Queue management for multiple toasts
 */

import React, { useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

export type ToastType = 'success' | 'error' | 'info' | 'warning' | 'auto_deposit';

export interface ToastData {
  id: string;
  type: ToastType;
  title: string;
  message: string;
  duration?: number;
  details?: Record<string, unknown>;
}

interface ToastProps {
  toast: ToastData;
  onDismiss: (id: string) => void;
}

const Toast: React.FC<ToastProps> = ({ toast, onDismiss }) => {
  const handleClick = useCallback(() => {
    onDismiss(toast.id);
  }, [toast.id, onDismiss]);

  const getToastStyles = (type: ToastType) => {
    const styles: Record<ToastType, { bg: string; border: string; icon: string; iconColor: string }> = {
      success: {
        bg: 'bg-green-900/90',
        border: 'border-green-500/50',
        icon: '\u2714',
        iconColor: 'text-green-400',
      },
      error: {
        bg: 'bg-red-900/90',
        border: 'border-red-500/50',
        icon: '\u2718',
        iconColor: 'text-red-400',
      },
      info: {
        bg: 'bg-blue-900/90',
        border: 'border-blue-500/50',
        icon: '\u2139',
        iconColor: 'text-blue-400',
      },
      warning: {
        bg: 'bg-yellow-900/90',
        border: 'border-yellow-500/50',
        icon: '\u26A0',
        iconColor: 'text-yellow-400',
      },
      auto_deposit: {
        bg: 'bg-gradient-to-r from-blue-900/90 to-purple-900/90',
        border: 'border-blue-400/50',
        icon: '\uD83D\uDE80',
        iconColor: 'text-blue-400',
      },
    };
    return styles[type] || styles.info;
  };

  const styles = getToastStyles(toast.type);

  return (
    <motion.div
      initial={{ opacity: 0, y: -20, scale: 0.95 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: -10, scale: 0.95 }}
      transition={{ type: 'spring', damping: 25, stiffness: 300 }}
      onClick={handleClick}
      className={`
        ${styles.bg} ${styles.border}
        border rounded-xl p-4 shadow-lg cursor-pointer
        backdrop-blur-sm min-w-[320px] max-w-[420px]
      `}
    >
      <div className="flex items-start gap-3">
        <span className={`text-xl flex-shrink-0 ${styles.iconColor}`}>
          {styles.icon}
        </span>
        <div className="flex-1 min-w-0">
          <p className="text-white font-semibold text-sm">
            {toast.title}
          </p>
          <p className="text-gray-300 text-sm mt-0.5">
            {toast.message}
          </p>
          {toast.type === 'auto_deposit' && toast.details && (
            <div className="flex gap-4 mt-2 text-xs">
              {toast.details.lp_amount !== undefined && Number(toast.details.lp_amount) > 0 && (
                <span className="text-purple-400">
                  Orca LP: ${Number(toast.details.lp_amount).toFixed(2)}
                </span>
              )}
              {toast.details.drift_amount !== undefined && Number(toast.details.drift_amount) > 0 && (
                <span className="text-cyan-400">
                  Drift: ${Number(toast.details.drift_amount).toFixed(2)}
                </span>
              )}
            </div>
          )}
        </div>
        <button
          onClick={(e) => {
            e.stopPropagation();
            handleClick();
          }}
          className="text-gray-400 hover:text-white transition-colors p-1"
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
    </motion.div>
  );
};

interface ToastContainerProps {
  toasts: ToastData[];
  onDismiss: (id: string) => void;
  position?: 'top-right' | 'top-left' | 'bottom-right' | 'bottom-left' | 'top-center';
}

export const ToastContainer: React.FC<ToastContainerProps> = ({
  toasts,
  onDismiss,
  position = 'top-right',
}) => {
  const getPositionClasses = () => {
    const positions: Record<string, string> = {
      'top-right': 'top-4 right-4',
      'top-left': 'top-4 left-4',
      'bottom-right': 'bottom-4 right-4',
      'bottom-left': 'bottom-4 left-4',
      'top-center': 'top-4 left-1/2 -translate-x-1/2',
    };
    return positions[position] || positions['top-right'];
  };

  return (
    <div className={`fixed ${getPositionClasses()} z-[100] flex flex-col gap-2`}>
      <AnimatePresence mode="popLayout">
        {toasts.map((toast) => (
          <Toast key={toast.id} toast={toast} onDismiss={onDismiss} />
        ))}
      </AnimatePresence>
    </div>
  );
};

export default Toast;
